import React from 'react';
import PropTypes from 'prop-types';
import { Cushion, Grid, MaxWidth, Rectangle, Shadow, Typography } from '@pitchero/react-ui';
import Comments from '../comments/dynamic';
import VideoPlayer from '../video/dynamic';
import HeaderBackgroundContainer from '../club-frame/header/background-container';
import clubUrl from '../../lib/routing/club-url';
import { Trans } from '../../lib/i18n';
import CLUB_PROP_TYPES from '../../lib/prop-types/club';
import RosTopBanner from '../adverts/ros-top-banner';
import VideoDetail from './video-detail';
import VideoDetailLoading from './video-detail-loading';
import VideoList from './video-list';
import BREAKPOINT from './breakpoint';

const Video = ({
  author,
  club,
  description,
  duration,
  isLoading,
  related,
  title,
  thumbnail,
  videoId,
  videoViews,
  videoUrl,
  nextVideo,
}) => {
  const videoOptions = {
    autoplay: true,
    controls: true,
    aspectRatio: '16:9',
    poster: thumbnail,
    videoUrl,
  };

  const shareUrl = clubUrl(club, 'media.video', { _title: title, id: videoId });

  return (
    <>
      <MaxWidth component="div" maxWidth={1366} center>
        <Cushion top="medium" responsive={[{ maxWidth: BREAKPOINT, props: { bottom: 'medium' } }]}>
          <RosTopBanner />
        </Cushion>
        <Cushion
          all="medium"
          responsive={[
            { maxWidth: BREAKPOINT, props: { horizontal: '0', bottom: 'large', top: '0' } },
          ]}
        >
          <Grid
            className="grid-video-page"
            columns="1fr 400px"
            responsive={[
              { maxWidth: 'desk', props: { columns: '1fr 324px' } },
              { maxWidth: BREAKPOINT, props: { display: 'block' } },
            ]}
            rowGap="large"
            columnGap="large"
          >
            <div>
              <Shadow>
                <Rectangle
                  fill="white"
                  radius="rounded"
                  responsive={[{ maxWidth: BREAKPOINT, props: { radius: '0' } }]}
                >
                  {videoUrl && (
                    <HeaderBackgroundContainer imgSrc={thumbnail} preset="card" height={300}>
                      <VideoPlayer
                        videoId={videoId}
                        videoOptions={videoOptions}
                        duration={duration}
                        key={`videoPlayer${videoId}`}
                        nextVideo={nextVideo}
                        coverImage={thumbnail}
                      />
                    </HeaderBackgroundContainer>
                  )}
                  <Cushion
                    all="medium"
                    responsive={[{ minWidth: 'lap', props: { all: 'large' } }]}
                    component="div"
                  >
                    {isLoading ? (
                      <VideoDetailLoading />
                    ) : (
                      <VideoDetail
                        club={club}
                        views={videoViews}
                        author={author}
                        description={description}
                        title={title}
                        shareUrl={shareUrl}
                        thumbnail={thumbnail}
                        videoId={videoId}
                      />
                    )}
                  </Cushion>
                </Rectangle>
              </Shadow>
              {!isLoading && (
                <Cushion
                  className="comments-wrap"
                  horizontal="medium"
                  vertical="large"
                  component="div"
                >
                  <Cushion bottom="medium">
                    <Typography preset="title" component="h3">
                      <Trans i18nKey="common:comments_title">Comments</Trans>
                    </Typography>
                  </Cushion>
                  <Comments club={club} mediaType="video" mediaId={videoId} />
                </Cushion>
              )}
            </div>
            <div className="video-page__sidebar">
              <Cushion
                component="div"
                responsive={[{ maxWidth: BREAKPOINT, props: { horizontal: 'small' } }]}
              >
                <VideoList videos={related} />
              </Cushion>
            </div>
          </Grid>
        </Cushion>
      </MaxWidth>
    </>
  );
};

Video.defaultProps = {
  author: {
    id: 0,
    name: '',
  },
  description: null,
  duration: null,
  videoViews: null,
  videoUrl: null,
  nextVideo: null,
};

Video.propTypes = {
  author: PropTypes.shape({
    id: PropTypes.number.isRequired,
    name: PropTypes.string.isRequired,
  }),
  club: CLUB_PROP_TYPES.isRequired,
  description: PropTypes.string,
  duration: PropTypes.number,
  isLoading: PropTypes.bool.isRequired,
  related: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
      thumbnail: PropTypes.string.isRequired,
      title: PropTypes.string.isRequired,
      url: PropTypes.string.isRequired,
    }),
  ).isRequired,
  thumbnail: PropTypes.string.isRequired,
  title: PropTypes.string.isRequired,
  videoId: PropTypes.number.isRequired,
  videoViews: PropTypes.number,
  videoUrl: PropTypes.string,
  nextVideo: PropTypes.shape(),
};

export default Video;
